/* ============================================================
   PROP & GROW — SEARCH  (instant results + search page engine)
   - Index: /collections/all?view=pg-search-index (cached 20 min)
   - Typo tolerant, common plant names, pet safe / light / growth words
   - Exact matches rank first even when sold out (marked "Sold out")
   ============================================================ */
(() => {
  if (window.PGSearch) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const THEME = (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || '';
  const CACHE_KEY = 'pg-search-index-v1:' + THEME;
  const CACHE_MS = 20 * 60 * 1000;

  // ---------- text helpers ----------
  const norm = s => String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[’‘`]/g, "'")
    .replace(/'s\b/g, 's')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const words = s => norm(s).split(' ').filter(Boolean);

  const STOP = new Set(['a', 'an', 'the', 'of', 'for', 'and', 'with', 'in', 'my', 'to', 'plant', 'plants']);

  // Common names -> what the catalog calls them. Keys and values are matched as whole words.
  const SYNONYMS = {
    'snake plant': ['sansevieria', 'dracaena trifasciata'],
    'mother in law tongue': ['sansevieria'],
    'mother in laws tongue': ['sansevieria'],
    'pothos': ['epipremnum', 'scindapsus'],
    'devils ivy': ['epipremnum'],
    'satin pothos': ['scindapsus'],
    'silver pothos': ['scindapsus'],
    'zz': ['zamioculcas'],
    'prayer plant': ['maranta', 'calathea', 'ctenanthe', 'stromanthe'],
    'wax plant': ['hoya'],
    'wax flower': ['hoya'],
    'sweetheart': ['kerrii', 'heart'],
    'elephant ear': ['alocasia', 'colocasia', 'xanthosoma', 'caladium'],
    'elephant ears': ['alocasia', 'colocasia', 'xanthosoma', 'caladium'],
    'rubber plant': ['ficus elastica'],
    'rubber tree': ['ficus elastica'],
    'fiddle leaf': ['ficus lyrata'],
    'swiss cheese': ['monstera'],
    'split leaf': ['monstera'],
    'chinese money': ['pilea'],
    'money plant': ['pilea', 'money tree'],
    'pancake plant': ['pilea'],
    'spider plant': ['chlorophytum', 'spider plants'],
    'peace lily': ['spathiphyllum'],
    'arrowhead': ['syngonium'],
    'heartleaf': ['cordatum', 'hederaceum'],
    'heart leaf': ['cordatum', 'hederaceum'],
    'radiator plant': ['peperomia'],
    'baby rubber': ['peperomia obtusifolia'],
    'nerve plant': ['fittonia'],
    'polka dot': ['hypoestes'],
    'lipstick': ['aeschynanthus', 'lipstick plants'],
    'dumb cane': ['dieffenbachia'],
    'chinese evergreen': ['aglaonema'],
    'wandering dude': ['tradescantia'],
    'wandering jew': ['tradescantia'],
    'inch plant': ['tradescantia'],
    'banana': ['musa'],
    'succulent': ['succulent', 'cactus', 'euphorbia', 'crassula', 'haworthia', 'echeveria', 'aloe', 'sedum', 'kalanchoe', 'gasteria', 'senecio', 'othonna', 'monadenium', 'caralluma'],
    'succulents': ['succulent', 'cactus', 'euphorbia', 'crassula', 'haworthia', 'echeveria', 'aloe', 'sedum', 'kalanchoe', 'gasteria', 'senecio', 'othonna', 'monadenium', 'caralluma'],
    'cacti': ['cactus'],
    'orchid': ['orchids'],
    'fern': ['ferns'],
    'carnivorous': ['carnivorous plants'],
    'fruit': ['edible and fruiting plants', 'fruiting'],
    'pet friendly': ['pet safe'],
    'pet safe': ['pet safe'],
    'cat safe': ['pet safe'],
    'dog safe': ['pet safe'],
    'non toxic': ['pet safe'],
    'nontoxic': ['pet safe'],
    'plush': ['plushie'],
    'plushy': ['plushie'],
    'stuffed': ['plushie'],
    'keychain': ['plushie', 'keychain'],
    'planter': ['pot'],
    'planters': ['pot'],
    'pots': ['pot'],
    'potting mix': ['soil', 'perlite', 'coir', 'sphagnum', 'bark', 'leca', 'substrate'],
    'soil': ['soil', 'perlite', 'coir', 'sphagnum', 'bark', 'leca', 'substrate', 'potting'],
    'fertiliser': ['fertilizer'],
    'plant food': ['fertilizer'],
    'hanging': ['trailing'],
    'vining': ['trailing', 'climbing'],
    'vine': ['trailing', 'climbing'],
    'variegated': ['variegated', 'variegata', 'variegation']
  };
  const SYN_KEYS = Object.keys(SYNONYMS).map(k => ({ key: k, toks: k.split(' ') }))
    .sort((a, b) => b.toks.length - a.toks.length);

  // Damerau-Levenshtein distance with a cut-off.
  const editDistance = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const m = a.length, n = b.length;
    let prev2 = null, prev = new Array(n + 1), cur = new Array(n + 1);
    for (let j = 0; j <= n; j++) prev[j] = j;
    for (let i = 1; i <= m; i++) {
      cur[0] = i;
      let rowMin = cur[0];
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev.slice(); [prev, cur] = [cur, prev];
    }
    return prev[n];
  };

  // ---------- index ----------
  let indexPromise = null;

  const prepare = raw => raw.map((p, n) => {
    const cols = String(p.k || '').split('|').filter(Boolean).map(s => {
      const [handle, title] = s.split('~');
      return { handle, title: title || handle };
    });
    const tags = String(p.tg || '').split('|').filter(Boolean);
    const facets = [];
    if (p.pet === 1) facets.push('pet safe non toxic');
    if (p.pet === 2) facets.push('toxic to pets');
    if (p.g && p.g !== 'Not Applicable') facets.push(p.g);
    if (p.l) facets.push(p.l + ' light');
    if (tags.includes('pg-best-seller')) facets.push('best seller bestseller popular');
    if (tags.includes('plushie')) facets.push('plushie plush toy gift');
    if (tags.includes('pg-restocked')) facets.push('restocked back in stock');
    // Some plants are missing the "plant" tag, so also look at the title
    const SUPPLY = /\b(plush|plushie|keychain|perlite|coir|fertilizer|spray|gnats?|moss|pots?|leaf shine|heat pack|rooting for u)\b/i;
    const isPlant = tags.includes('plant') || (!tags.includes('plushie') && !SUPPLY.test(p.t));
    return {
      n,
      title: p.t,
      url: p.u,
      image: p.i,
      price: p.p,
      compare: p.c,
      variants: p.v,
      available: p.a === 1,
      pet: p.pet,
      created: p.d,
      cols,
      isPlant,
      tags,
      normTitle: norm(p.t),
      fTitle: words(p.t),
      fCols: words(cols.map(c => c.title).join(' ')),
      fFacets: words(facets.join(' ')),
      fDesc: words(p.x)
    };
  });

  const fetchPage = page =>
    fetch(`${ROOT}collections/all?view=pg-search-index&page=${page}`, { credentials: 'same-origin' })
      .then(r => { if (!r.ok) throw new Error('index ' + r.status); return r.json(); });

  const loadIndex = () => {
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.at < CACHE_MS && Array.isArray(cached.products)) {
          return prepare(cached.products);
        }
      } catch (e) { /* ignore */ }
      const first = await fetchPage(1);
      const rest = [];
      for (let pg = 2; pg <= Math.min(first.pages || 1, 12); pg++) rest.push(fetchPage(pg));
      const pages = [first, ...(await Promise.all(rest))];
      const products = pages.flatMap(pg => pg.products || []);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), products })); } catch (e) { /* full */ }
      return prepare(products);
    })().catch(err => { indexPromise = null; throw err; });
    return indexPromise;
  };

  // ---------- query parsing ----------
  // Returns groups; every group must match. A group is a list of alternatives,
  // each alternative a list of tokens that must all match.
  const parseQuery = q => {
    const full = words(q);
    const meaningful = full.filter(t => !STOP.has(t));
    const groups = [];
    // synonyms are looked for in the full query (so "snake plant" still works)
    const used = new Set();
    for (const { key, toks: kt } of SYN_KEYS) {
      for (let i = 0; i + kt.length <= full.length; i++) {
        let ok = true;
        for (let j = 0; j < kt.length; j++) if (full[i + j] !== kt[j] || used.has(i + j)) { ok = false; break; }
        if (!ok) continue;
        for (let j = 0; j < kt.length; j++) used.add(i + j);
        const literal = kt.filter(t => !STOP.has(t));
        const alts = SYNONYMS[key].map(v => v.split(' '));
        if (literal.length) alts.unshift(literal);
        groups.push({ alts, label: key });
      }
    }
    full.forEach((t, i) => {
      if (used.has(i)) return;
      if (STOP.has(t) && meaningful.length) return;
      groups.push({ alts: [[t]], label: t });
    });
    return groups;
  };

  // score one query token against a field's tokens; 0 = no match
  const tokenScore = (qt, fieldToks, allowFuzzy, memo) => {
    let best = 0;
    for (const ft of fieldToks) {
      if (ft === qt) return 1;
      if (ft === qt + 's' || ft === qt + 'es' || qt === ft + 's') { best = Math.max(best, 0.95); continue; }
      let s = 0;
      if (ft.startsWith(qt) && qt.length >= 2) s = qt.length >= 3 ? 0.85 : 0.6;
      else if (qt.length >= 4 && ft.includes(qt)) s = 0.55;
      else if (allowFuzzy && qt.length >= 4 && ft.length >= 3) {
        const key = qt + '|' + ft;
        let d = memo.get(key);
        if (d === undefined) {
          const max = qt.length >= 8 ? 2 : 1;
          // whole word, or the start of a longer word typed with a typo ("filoden" -> "philodendron")
          d = Math.min(editDistance(qt, ft, max), ft.length > qt.length ? editDistance(qt, ft.slice(0, qt.length), max) : 99);
          if (d > max) d = 99;
          memo.set(key, d);
        }
        if (d === 1) s = 0.7; else if (d === 2) s = 0.55;
      }
      if (s > best) best = s;
    }
    return best;
  };

  const FIELDS = [
    ['fTitle', 10, true],
    ['fCols', 7, true],
    ['fFacets', 5, false],
    ['fDesc', 1.5, false]
  ];

  const scoreItem = (item, groups, memo) => {
    let total = 0;
    for (const g of groups) {
      let bestAlt = 0;
      for (const alt of g.alts) {
        let altScore = 0;
        let ok = true;
        for (const qt of alt) {
          let best = 0;
          for (const [field, weight, fuzzy] of FIELDS) {
            const s = tokenScore(qt, item[field], fuzzy, memo) * weight;
            if (s > best) best = s;
          }
          if (!best) { ok = false; break; }
          altScore += best;
        }
        if (ok) bestAlt = Math.max(bestAlt, altScore / alt.length);
      }
      if (!bestAlt) return 0;
      total += bestAlt;
    }
    return total;
  };

  const search = (index, q, opts = {}) => {
    const groups = parseQuery(q);
    if (!groups.length) return { items: [], collections: [], groups };
    const memo = new Map();
    const nq = norm(q);
    const reEsc = nq.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const phrase = nq.length >= 3 ? new RegExp('(^| )' + reEsc + '(s|es)?( |$)') : null;
    const startPhrase = nq.length >= 3 ? new RegExp('^' + reEsc + '(s|es)?( |$)') : null;
    const wantsPlush = /plush|keychain|toy|stuffed/.test(nq);
    const out = [];
    for (const item of index) {
      let s = scoreItem(item, groups, memo);
      if (!s) continue;
      if (phrase && phrase.test(item.normTitle)) s += 12;
      if (startPhrase && startPhrase.test(item.normTitle)) s += 4;
      out.push({ item, s });
    }
    // drop weak tail matches when there are strong ones (e.g. description-only hits)
    const top = out.reduce((m, r) => Math.max(m, r.s), 0);
    const kept = out.filter(r => r.s >= top * 0.3);
    // then prefer in-stock plants over sold-out ones and over plush toys
    kept.forEach(r => {
      if (r.item.available) r.s += 4;
      if (!wantsPlush && !r.item.isPlant) r.s -= 3;
    });
    kept.sort((a, b) => b.s - a.s || (b.item.available - a.item.available) || a.item.n - b.item.n);

    // matching collections, by how many results they hold
    const colCount = new Map();
    kept.forEach(({ item }) => item.cols.forEach(c => {
      const nc = norm(c.title);
      const hit = groups.some(g => g.alts.some(alt => alt.every(t => nc.split(' ').some(w => w === t || w.startsWith(t) || (t.length >= 5 && editDistance(t, w, 2) <= (t.length >= 8 ? 2 : 1))))));
      if (!hit) return;
      const e = colCount.get(c.handle) || { ...c, count: 0 };
      e.count++; colCount.set(c.handle, e);
    }));
    const collections = [...colCount.values()].sort((a, b) => b.count - a.count).slice(0, opts.maxCollections || 4);
    return { items: kept.map(r => r.item), collections, groups };
  };

  // Closest title word for "Did you mean"
  const suggest = (index, q) => {
    const qt = words(q).filter(t => !STOP.has(t));
    if (!qt.length) return '';
    const vocab = new Set();
    index.forEach(it => { it.fTitle.forEach(w => w.length > 3 && vocab.add(w)); it.fCols.forEach(w => w.length > 3 && vocab.add(w)); });
    const fixed = qt.map(t => {
      let best = t, bd = 3;
      vocab.forEach(w => { const d = editDistance(t, w, 3); if (d < bd) { bd = d; best = w; } });
      return best;
    });
    const s = fixed.join(' ');
    return s !== qt.join(' ') ? s : '';
  };

  // ---------- formatting ----------
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  let fmt;
  try { fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }); }
  catch (e) { fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }); }
  const money = cents => fmt.format((cents || 0) / 100);
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const searchUrl = q => `${ROOT}search?q=${encodeURIComponent(q)}`;

  const priceHtml = it => {
    const from = it.variants > 1 ? '<span class="pgs-from">From</span> ' : '';
    const cmp = it.compare && it.compare > it.price ? ` <s>${money(it.compare)}</s>` : '';
    return `${from}${money(it.price)}${cmp}`;
  };

  // ---------- instant results drop-down ----------
  const POPULAR = ['Hoya', 'Monstera', 'Alocasia', 'Philodendron', 'Pet safe', 'Low light', 'Trailing', 'Pothos'];

  const panel = document.createElement('div');
  panel.className = 'pgs-panel';
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;
  let activeInput = null;
  let activeIndex = -1;
  let lastQuery = null;

  const placePanel = () => {
    if (!activeInput) return;
    const r = activeInput.getBoundingClientRect();
    // the search box was hidden (e.g. the mobile search bar closed): don't leave the results floating
    if (!r.width || !r.height || !activeInput.offsetParent) { closePanel(); return; }
    const vw = window.innerWidth;
    const mobile = vw < 750;
    const width = mobile ? vw - 16 : Math.max(r.width, 420);
    let left = mobile ? 8 : Math.min(r.right - width, vw - width - 12);
    if (!mobile && left < 12) left = 12;
    panel.style.top = `${Math.round(r.bottom + 8)}px`;
    panel.style.left = `${Math.round(left)}px`;
    panel.style.width = `${Math.round(width)}px`;
    panel.style.maxHeight = `${Math.max(240, window.innerHeight - r.bottom - 24)}px`;
  };

  const openPanel = () => {
    if (!panel.isConnected) document.body.appendChild(panel);
    panel.hidden = false;
    placePanel();
  };
  // close when the mobile search bar or the menu is closed
  document.addEventListener('click', e => {
    if (e.target.closest('[data-pgh-search-toggle], [data-pgh-menu-open]')) setTimeout(() => { if (activeInput && !activeInput.offsetParent) closePanel(); }, 50);
  });
  const closePanel = () => {
    panel.hidden = true;
    activeIndex = -1;
    if (activeInput) activeInput.setAttribute('aria-expanded', 'false');
  };

  const optionEls = () => Array.from(panel.querySelectorAll('[data-pgs-option]'));
  const setActive = i => {
    const opts = optionEls();
    if (!opts.length) return;
    activeIndex = (i + opts.length) % opts.length;
    opts.forEach((o, k) => o.classList.toggle('is-active', k === activeIndex));
    opts[activeIndex].scrollIntoView({ block: 'nearest' });
  };

  const renderEmpty = () => {
    panel.innerHTML = `
      <div class="pgs-panel__section">
        <p class="pgs-panel__label">Popular searches</p>
        <div class="pgs-chips">${POPULAR.map(p => `<a class="pgs-chip" data-pgs-option href="${searchUrl(p)}">${esc(p)}</a>`).join('')}</div>
      </div>`;
  };

  const renderResults = (q, res, index) => {
    const items = res.items.slice(0, 6);
    if (!items.length) {
      const dym = suggest(index, q);
      panel.innerHTML = `
        <div class="pgs-panel__section pgs-panel__none">
          <p>No plants match “${esc(q)}”.</p>
          ${dym ? `<p>Did you mean <a data-pgs-option href="${searchUrl(dym)}">${esc(dym)}</a>?</p>` : ''}
        </div>
        <div class="pgs-panel__section">
          <p class="pgs-panel__label">Popular searches</p>
          <div class="pgs-chips">${POPULAR.map(p => `<a class="pgs-chip" data-pgs-option href="${searchUrl(p)}">${esc(p)}</a>`).join('')}</div>
        </div>`;
      return;
    }
    const cols = res.collections.length ? `
      <div class="pgs-panel__section">
        <div class="pgs-chips">${res.collections.map(c => `<a class="pgs-chip pgs-chip--col" data-pgs-option href="${ROOT}collections/${esc(c.handle)}">${esc(c.title)} <span>${c.count}</span></a>`).join('')}</div>
      </div>` : '';
    panel.innerHTML = `
      ${cols}
      <ul class="pgs-list">
        ${items.map(it => `
          <li>
            <a class="pgs-item" data-pgs-option href="${esc(it.url)}">
              <span class="pgs-item__img">${it.image ? `<img src="${esc(it.image)}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
              <span class="pgs-item__body">
                <span class="pgs-item__title">${esc(it.title)}</span>
                <span class="pgs-item__meta">
                  <span class="pgs-item__price">${priceHtml(it)}</span>
                  ${it.available ? '<span class="pgs-pill pgs-pill--in">In stock</span>' : '<span class="pgs-pill pgs-pill--out">Sold out</span>'}
                </span>
              </span>
            </a>
          </li>`).join('')}
      </ul>
      <a class="pgs-panel__all" data-pgs-option href="${searchUrl(q)}">See all ${res.items.length} result${res.items.length === 1 ? '' : 's'} for “${esc(q)}” →</a>`;
  };

  const update = async () => {
    if (!activeInput) return;
    const q = activeInput.value.trim();
    if (q === lastQuery && !panel.hidden) return;
    lastQuery = q;
    openPanel();
    activeIndex = -1;
    activeInput.setAttribute('aria-expanded', 'true');
    if (!q) { renderEmpty(); return; }
    let index;
    try {
      if (!panel.innerHTML) panel.innerHTML = '<p class="pgs-panel__loading">Searching…</p>';
      index = await loadIndex();
    } catch (e) {
      panel.innerHTML = `<a class="pgs-panel__all" data-pgs-option href="${searchUrl(q)}">Search for “${esc(q)}” →</a>`;
      return;
    }
    if (!activeInput || activeInput.value.trim() !== q) return;
    renderResults(q, search(index, q), index);
  };

  let timer = null;
  let ignoreInputUntil = 0; // Esc clears a search box and fires "input"; don't reopen because of that
  const attach = input => {
    if (!input || input.dataset.pgsAttached) return;
    input.dataset.pgsAttached = '1';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.addEventListener('focus', () => { activeInput = input; lastQuery = null; loadIndex().catch(() => {}); update(); });
    input.addEventListener('input', () => {
      if (Date.now() < ignoreInputUntil) return;
      activeInput = input; clearTimeout(timer); timer = setTimeout(update, 90);
    });
    input.addEventListener('keydown', e => {
      if (e.key === 'Escape') { ignoreInputUntil = Date.now() + 400; clearTimeout(timer); }
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === 'Escape') { closePanel(); }
      else if (e.key === 'Enter' && activeIndex >= 0) {
        const o = optionEls()[activeIndex];
        if (o) { e.preventDefault(); window.location.href = o.href; }
      }
    });
  };

  document.addEventListener('pointerdown', e => {
    if (panel.hidden) return;
    if (panel.contains(e.target) || (activeInput && activeInput.contains(e.target))) return;
    closePanel();
  });
  window.addEventListener('resize', () => { if (!panel.hidden) placePanel(); });
  window.addEventListener('scroll', () => { if (!panel.hidden) placePanel(); }, { passive: true });

  const attachAll = () => document.querySelectorAll('[data-pgs-input]').forEach(attach);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attachAll); else attachAll();

  window.PGSearch = { loadIndex, search, suggest, money, esc, priceHtml, searchUrl, POPULAR, attach, closePanel, norm };
})();
